import { FC } from "react";
import {
  Paper,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { ExportDataItemDto } from "../../../../../../../api-client";
import { formatDateUI } from "../../../../../../../helpers/dateFormat";

interface ExportListPanelProps {
  exportData: ExportDataItemDto[];
  onExportClick: (exportItem: ExportDataItemDto) => void;
  selectedExportId: string | null;
}

export const ExportListPanel: FC<ExportListPanelProps> = ({
  exportData,
  onExportClick,
  selectedExportId,
}) => {
  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "#D9DFE2",
        border: "1px solid #e0e0e0",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          bgcolor: "white",
          color: "black",
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <FileUploadIcon fontSize="small" sx={{ color: "#757575" }} />
        <Typography variant="body2" fontWeight="500">
          Export-Daten
        </Typography>
        {exportData.length > 0 && (
          <Chip
            label={exportData.length}
            size="small"
            sx={{
              bgcolor: "#003059",
              color: "white",
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 500,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1,
          bgcolor: "#D9DFE2",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f5f5f5",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#bdbdbd",
            borderRadius: "6px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#9e9e9e",
          },
        }}
      >
        {exportData.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <FileUploadIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="caption" align="center">
              Keine Export-Daten
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {exportData.map((exportItem, index) => (
              <ListItemButton
                key={exportItem.id}
                onClick={() => onExportClick(exportItem)}
                selected={selectedExportId === exportItem.id}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  mb: 0.5,
                  bgcolor:
                    selectedExportId === exportItem.id
                      ? "#c8e4fa !important"
                      : "white",
                  borderColor:
                    selectedExportId === exportItem.id ? "#003059" : "#e0e0e0",
                  "&:hover": {
                    bgcolor:
                      selectedExportId === exportItem.id
                        ? "#bbdefb"
                        : "#f5f5f5",
                    borderColor: "#003059",
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 0.5,
                        pb: 1.5,
                        pt: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        color="#003059"
                      >
                        {`Export #${index + 1}`}
                      </Typography>
                      {exportItem.metadata?.creationDate && (
                        <Typography
                          variant="body2"
                          fontWeight="500"
                          color="text.secondary"
                        >
                          {formatDateUI(exportItem.metadata?.creationDate)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Paper>
  );
};
